/**
 * ═══════════════════════════════════════════════════════════════════
 *  PracticePrescriptionPage — Guided Sandbox for New Doctors
 * ═══════════════════════════════════════════════════════════════════
 *
 *  Flow:
 *    1. Create a practice visit+patient (backend)
 *    2. Doctor fills patient info (name, age, sex) via PatientInfoEditable
 *    3. Vitals auto-filled from specialty seed data (editable)
 *    4. Tour guides through DigitalPrescriptionForm step-by-step
 *    5. Doctor writes a real prescription → generates real PDF
 *    6. On tour complete → cleanup (delete visit+patient+metadata) → navigate back
 *
 *  Everything is deleted at the end — zero trace.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext';
import { TourProvider, useTour } from '../modules/tour';
import { buildPrescriptionTourSteps, getSampleShorthand } from '../modules/tour/configs/prescriptionTourSteps';
import PatientInfoEditable from '../components/common/PatientInfoEditable/PatientInfoEditable';
import DigitalPrescriptionForm from '../components/visit/DigitalPrescriptionForm';
import useVisitStore from '../stores/visitStore';
import {
  fetchTourStatus,
  createPracticeVisit,
  cleanupPracticeVisit,
  updateTourStatus,
} from '../services/api/prescriptionTourApi';
import { ROUTES } from '../constants/routes';
import { specialisationName } from '../constants/staff';

// ── Styles ────────────────────────────────────────────────────────────

const bannerStyle = (isDark) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.5rem',
  padding: '0.5rem 1rem',
  background: isDark
    ? 'linear-gradient(135deg, rgba(245,158,11,0.12), rgba(217,119,6,0.06))'
    : 'linear-gradient(135deg, rgba(245,158,11,0.08), rgba(217,119,6,0.04))',
  borderBottom: `1px solid ${isDark ? 'rgba(245,158,11,0.2)' : 'rgba(245,158,11,0.15)'}`,
  fontSize: '0.75rem',
  fontWeight: 600,
  color: isDark ? '#f59e0b' : '#d97706',
  fontFamily: 'system-ui, sans-serif',
});

const containerStyle = (isDark) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  background: isDark ? '#0f0f11' : '#f8fafc',
  fontFamily: 'system-ui, sans-serif',
});

const headerStyle = (isDark) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0.75rem 1rem',
  borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
  background: isDark ? '#18181b' : '#ffffff',
});

const bodyStyle = {
  flex: 1,
  overflow: 'auto',
  padding: '1rem',
};

const patientCardStyle = (isDark) => ({
  padding: '0.75rem 1rem',
  borderRadius: '0.75rem',
  border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
  background: isDark ? '#1c1c20' : '#ffffff',
  marginBottom: '0.75rem',
});

const loadingStyle = (isDark) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100vh',
  gap: '1rem',
  fontFamily: 'system-ui, sans-serif',
  background: isDark ? '#0f0f11' : '#f8fafc',
  color: isDark ? '#e5e7eb' : '#374151',
});

const errorStyle = (isDark) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100vh',
  gap: '1rem',
  fontFamily: 'system-ui, sans-serif',
  background: isDark ? '#0f0f11' : '#f8fafc',
  color: isDark ? '#ef4444' : '#dc2626',
});


// ═════════════════════════════════════════════════════════════════════
// Inner page — rendered inside TourProvider
// ═════════════════════════════════════════════════════════════════════

function PracticePageContent({ practiceData, onFinish }) {
  const { isDark } = useTheme();
  const tour = useTour();
  const bodyRef = useRef(null);
  const [formKey] = useState(0);
  const [visitState, setVisitState] = useState({
    visit_id: practiceData.visit_id,
    patient_id: practiceData.patient_id,
    patient_name: '',
    patient_age: '',
    patient_gender: '',
    patient_mobile: '0000000000',
    queue_number: 0,
    doctor_member_id: practiceData.doctor_member_id,
    doctor_name: practiceData.doctor_name,
    doctor_specialization: practiceData.specialization,
    status: 'IN_PROGRESS',
    is_practice: true,
  });

  // Propagate patient info updates from PatientInfoEditable into local state
  const handlePatientUpdate = useCallback((updates) => {
    setVisitState((prev) => ({ ...prev, ...updates }));
  }, []);

  // Prescription save success callback — advance tour to completion step
  const handlePrescriptionSuccess = useCallback(() => {
    if (tour && tour.isActive) {
      setTimeout(() => tour.completeTour(), 800);
    }
  }, [tour]);

  return (
    <div style={containerStyle(isDark)}>
      {/* ── Practice Banner ──────────────────────────────────── */}
      <div style={bannerStyle(isDark)}>
        <span>🎓</span>
        <span>Practice Mode — This is for learning only. Everything will be deleted after practice.</span>
      </div>

      {/* ── Header ───────────────────────────────────────────── */}
      <div style={headerStyle(isDark)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
          <span style={{ fontSize: '1.125rem' }}>📝</span>
          <div>
            <p style={{
              margin: 0,
              fontSize: '0.875rem',
              fontWeight: 600,
              color: isDark ? '#f5f5f7' : '#111827',
            }}>
              Prescription Practice
            </p>
            <p style={{
              margin: 0,
              fontSize: '0.6875rem',
              color: isDark ? '#6b7280' : '#9ca3af',
            }}>
              {specialisationName(practiceData.specialization) || practiceData.specialization}
              {' · '}Dr. {practiceData.doctor_name}
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {tour && (
            <button
              onClick={() => tour.startTour()}
              style={{
                cursor: 'pointer',
                padding: '0.375rem 0.625rem',
                borderRadius: '0.375rem',
                border: `1px solid ${isDark ? 'rgba(20,184,166,0.2)' : 'rgba(20,184,166,0.15)'}`,
                background: isDark ? 'rgba(20,184,166,0.08)' : 'rgba(20,184,166,0.04)',
                fontSize: '0.6875rem',
                fontWeight: 500,
                color: '#14b8a6',
              }}
            >
              🔄 Restart Guide
            </button>
          )}
          <button
            onClick={onFinish}
            style={{
              cursor: 'pointer',
              padding: '0.375rem 0.625rem',
              borderRadius: '0.375rem',
              border: `1px solid ${isDark ? 'rgba(239,68,68,0.2)' : 'rgba(239,68,68,0.15)'}`,
              background: isDark ? 'rgba(239,68,68,0.06)' : 'rgba(239,68,68,0.04)',
              fontSize: '0.6875rem',
              fontWeight: 500,
              color: isDark ? '#f87171' : '#ef4444',
            }}
          >
            ✕ Exit Practice
          </button>
        </div>
      </div>

      {/* ── Body ─────────────────────────────────────────────── */}
      <div ref={bodyRef} style={bodyStyle}>
        {/* Patient Info Card */}
        <div style={patientCardStyle(isDark)} data-tour="patient-info">
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '0.375rem',
          }}>
            <span style={{ fontSize: '0.75rem' }}>🧑</span>
            <span style={{
              fontSize: '0.6875rem',
              fontWeight: 600,
              color: isDark ? '#9ca3af' : '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}>
              Patient Details
            </span>
            <span style={{
              fontSize: '0.5625rem',
              padding: '0.0625rem 0.3125rem',
              borderRadius: '0.25rem',
              background: isDark ? 'rgba(20,184,166,0.12)' : 'rgba(20,184,166,0.08)',
              color: '#14b8a6',
              fontWeight: 600,
            }}>
              Type name, then click age &amp; gender
            </span>
          </div>
          <PatientInfoEditable
            visit={visitState}
            isDark={isDark}
            editable
            autoEdit
            onUpdate={handlePatientUpdate}
          />
        </div>

        {/* Prescription Form */}
        <div style={{
          borderRadius: '0.75rem',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
          background: isDark ? '#1c1c20' : '#ffffff',
          padding: '0.5rem 0.75rem',
        }}>
          <DigitalPrescriptionForm
            key={formKey}
            visitId={practiceData.visit_id}
            patientId={practiceData.patient_id}
            onSuccess={handlePrescriptionSuccess}
            doctorSpecialization={practiceData.specialization}
            practiceMode
          />
        </div>
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════
// Main Page Component
// ═════════════════════════════════════════════════════════════════════

export default function PracticePrescriptionPage() {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [practiceData, setPracticeData] = useState(null);
  const cleanupRef = useRef(false);
  const practiceDataRef = useRef(null);

  // Ref to cache the creation promise — prevents React.StrictMode from
  // creating two practice visits on double-mount in dev mode.
  const initPromiseRef = useRef(null);

  // Keep ref in sync for cleanup on unmount
  useEffect(() => {
    practiceDataRef.current = practiceData;
  }, [practiceData]);

  // ── Initialize: create practice visit ──────────────────────
  useEffect(() => {
    let cancelled = false;

    // Only kick off the async creation once — StrictMode's second mount
    // reuses the same promise so only ONE visit is created.
    if (!initPromiseRef.current) {
      initPromiseRef.current = (async () => {
        const status = await fetchTourStatus();
        if (!status || status.role === 'UNKNOWN') {
          throw new Error('Unable to verify your profile. Please try again.');
        }
        return await createPracticeVisit(status.specialization);
      })();
    }

    setLoading(true);
    initPromiseRef.current
      .then((data) => {
        if (!cancelled) {
          setPracticeData(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[PracticePrescription] Init error:', err);
          setError(err.message || 'Failed to set up practice mode. Please try again.');
          setLoading(false);
        }
      });

    // Cleanup on unmount — delete practice data
    return () => {
      cancelled = true;
      const pd = practiceDataRef.current;
      if (pd && !cleanupRef.current) {
        cleanupRef.current = true;
        // Fire-and-forget cleanup + store refresh
        cleanupPracticeVisit(pd.visit_id, pd.patient_id)
          .then(() => useVisitStore.getState().fetchDailyVisits())
          .catch(() => {});
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Finish / Exit handler ──────────────────────────────────
  const handleFinish = useCallback(async () => {
    if (practiceData && !cleanupRef.current) {
      cleanupRef.current = true;
      try {
        await Promise.all([
          cleanupPracticeVisit(practiceData.visit_id, practiceData.patient_id),
          updateTourStatus(true),
        ]);
      } catch (err) {
        console.warn('[PracticePrescription] Cleanup error:', err);
      }
      // Refresh the visit store so dashboard/summary don't show stale practice data
      try {
        await useVisitStore.getState().fetchDailyVisits();
      } catch (_) { /* swallow */ }
    }
    navigate(ROUTES.ORG, { replace: true });
  }, [practiceData, navigate]);

  // ── Tour complete handler ──────────────────────────────────
  const handleTourComplete = useCallback(() => {
    // Don't auto-navigate — let the doctor explore or save first.
    // They'll click "Exit Practice" when done.
  }, []);

  // ── Loading state ──────────────────────────────────────────
  if (loading) {
    return (
      <div style={loadingStyle(isDark)}>
        <div style={{
          width: '2rem', height: '2rem',
          border: `3px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          borderTopColor: '#14b8a6',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
        <p style={{ fontSize: '0.875rem', fontWeight: 500 }}>
          Setting up practice mode...
        </p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ── Error state ────────────────────────────────────────────
  if (error || !practiceData) {
    return (
      <div style={errorStyle(isDark)}>
        <span style={{ fontSize: '2rem' }}>⚠️</span>
        <p style={{ fontSize: '0.875rem', fontWeight: 500 }}>
          {error || 'Something went wrong'}
        </p>
        <button
          onClick={() => navigate(ROUTES.ORG, { replace: true })}
          style={{
            cursor: 'pointer',
            padding: '0.5rem 1rem',
            borderRadius: '0.5rem',
            border: 'none',
            background: '#14b8a6',
            color: '#fff',
            fontSize: '0.8125rem',
            fontWeight: 600,
          }}
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  // ── Build tour steps ───────────────────────────────────────
  const steps = buildPrescriptionTourSteps({
    specialization: practiceData.specialization,
    sampleShorthand: getSampleShorthand(practiceData.specialization),
  });

  // ── Render ─────────────────────────────────────────────────
  return (
    <TourProvider
      tourId="prescription-practice"
      steps={steps}
      autoStart
      onComplete={handleTourComplete}
      onSkip={handleTourComplete}
      isDark={isDark}
    >
      <PracticePageContent
        practiceData={practiceData}
        onFinish={handleFinish}
      />
    </TourProvider>
  );
}
